import { useState, useEffect } from 'react'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
  Link as LinkIcon,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp,
  Copy,
  ShoppingCart,
  Plus,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/DataTable'
import { PaymentLink } from '@/types'
import { dashboardService } from '@/services/dashboardService'
import { formatDistanceToNow } from 'date-fns'
import { TableColumn, TableAction, TablePagination, TableSort, TableFilter } from '@/types/table'

type ApiResponse<T> = {
  success: boolean
  message: string
  data: {
    results: T[]
    count: number
    next: string | null
    previous: string | null
  }
}

const statusConfig = {
  active: {
    label: 'Active',
    color: 'text-green-600 bg-green-50 border-green-200',
    icon: CheckCircle,
  },
  inactive: {
    label: 'Inactive',
    color: 'text-gray-600 bg-gray-50 border-gray-200',
    icon: XCircle,
  },
  expired: {
    label: 'Expired',
    color: 'text-orange-600 bg-orange-50 border-orange-200',
    icon: AlertCircle,
  },
}

const currencyConfig = {
  USD: { label: 'USD', symbol: '$' },
  EUR: { label: 'EUR', symbol: '€' },
  GBP: { label: 'GBP', symbol: '£' },
  KES: { label: 'KES', symbol: 'KSh' },
  NGN: { label: 'NGN', symbol: '₦' },
  GHS: { label: 'GHS', symbol: '₵' },
}

export default function PaymentLinks() {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const [paymentLinks, setPaymentLinks] = useState<PaymentLink[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [pagination, setPagination] = useState<TablePagination>({
    current: 1,
    pageSize: 10,
    total: 0,
    pageSizeOptions: [5, 10, 20, 50],
  })
  const [sort, setSort] = useState<TableSort | undefined>({
    key: 'created_at',
    direction: 'desc',
  })
  const [filters, setFilters] = useState<TableFilter[]>([])

  const fetchPaymentLinks = async (params: {
    page: number
    pageSize: number
    sort?: TableSort
    filters: TableFilter[]
    search?: string
  }) => {
    setLoading(true)
    setError(null)
    try {
      // Convert filters to API parameters
      const isActiveFilter = filters.find(f => f.key === 'is_active')
      const saleShopFilter = filters.find(f => f.key === 'sale_shop')

      const response: ApiResponse<PaymentLink> = await dashboardService.getPaymentLinks({
        page: params.page,
        limit: params.pageSize,
        is_active:
          isActiveFilter?.value !== undefined ? isActiveFilter.value === 'true' : undefined,
        sale_shop: saleShopFilter?.value ? parseInt(saleShopFilter.value) : undefined,
        search: params.search,
      })

      if (response.success && response.data) {
        const { results = [], count = 0 } = response.data

        setPaymentLinks(results)
        setTotalCount(count)
        setPagination(prev => ({
          ...prev,
          current: params.page,
          total: count,
        }))
        return { data: results, total: count }
      } else {
        const errorMessage = response.message || 'Failed to fetch payment links'
        setError(errorMessage)
        return { data: [], total: 0 }
      }
    } catch (error: any) {
      let errorMessage = 'Failed to fetch payment links'

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }

      console.error('Failed to fetch payment links:', {
        error,
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data,
      })

      setError(errorMessage)
      setPaymentLinks([])
      setTotalCount(0)
      setPagination(prev => ({ ...prev, total: 0 }))

      return { data: [], total: 0 }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPaymentLinks({
      page: pagination.current,
      pageSize: pagination.pageSize,
      sort,
      filters,
      search: '',
    })
  }, [])

  const getStatusConfig = (paymentLink: PaymentLink) => {
    if (paymentLink.is_expired) {
      return statusConfig.expired
    }
    return paymentLink.is_active ? statusConfig.active : statusConfig.inactive
  }

  const formatAmount = (amount: string, currencyCode?: string) => {
    const currency =
      currencyConfig[currencyCode as keyof typeof currencyConfig] || currencyConfig.USD
    return `${currency.symbol}${parseFloat(amount).toFixed(2)}`
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
    }
  }

  // Define table columns
  const columns: TableColumn<PaymentLink>[] = [
    {
      key: 'title',
      title: 'Payment Link',
      dataIndex: 'title',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (_, record) => (
        <div>
          <p className="font-medium text-white">{record.title}</p>
          {record.description && (
            <p className="text-sm text-gray-300 truncate max-w-xs">{record.description}</p>
          )}
          <p className="text-xs text-gray-400 font-mono">{record.slug_link}</p>
        </div>
      ),
    },
    {
      key: 'amount',
      title: 'Amount',
      dataIndex: 'amount',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (_, record) => (
        <div>
          <p className="font-semibold text-white">
            {formatAmount(record.amount, record.currency_code)}
          </p>
          <p className="text-xs text-gray-300">{record.currency_name || 'USD'}</p>
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'is_active',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Active', value: 'true' },
        { label: 'Inactive', value: 'false' },
      ],
      render: (_, record) => {
        const config = getStatusConfig(record)
        const StatusIcon = config.icon
        return (
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border`} style={{ backgroundColor: statusColors.info.backgroundColor, borderColor: statusColors.info.borderColor }}
          >
            <StatusIcon className="w-3 h-3 mr-1" style={{ color: statusColors.info.color }} />
            {config.label}
          </span>
        )
      },
    },
    {
      key: 'sale_shop_name',
      title: 'Sale Shop',
      dataIndex: 'sale_shop_name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (_, record) => (
        <div className="flex items-center">
          <ShoppingCart className="w-4 h-4 mr-2 text-gray-400" style={{ color: statusColors.info.color }} />
          <span className="text-sm text-white" style={pageStyles.text}>{record.sale_shop_name || 'N/A'}</span>
        </div>
      ),
    },
    {
      key: 'multiple_use',
      title: 'Usage',
      dataIndex: 'multiple_use',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Multiple Use', value: 'true' },
        { label: 'Single Use', value: 'false' },
      ],
      render: value => (
        <span className={`text-sm ${value ? 'text-green-400' : 'text-orange-400'}`} style={{ color: value ? statusColors.success.color : statusColors.warning.color }}>
          {value ? 'Multiple' : 'Single'}
        </span>
      ),
    },
    {
      key: 'expiration_date',
      title: 'Expires',
      dataIndex: 'expiration_date',
      sortable: true,
      filterable: false,
      render: value => (
        <div>
          {value ? (
            <>
              <p className="text-sm text-white" style={pageStyles.text}>{new Date(value).toLocaleDateString()}</p>
              <p className="text-xs text-gray-300" style={pageStyles.textSecondary}>
                {formatDistanceToNow(new Date(value), { addSuffix: true })}
              </p>
            </>
          ) : (
            <span className="text-sm text-gray-400" style={pageStyles.textSecondary}>Never</span>
          )}
        </div>
      ),
    },
    {
      key: 'created_at',
      title: 'Created',
      dataIndex: 'created_at',
      sortable: true,
      filterable: false,
      render: value => (
        <div>
          <p className="text-sm text-white" style={pageStyles.text}>{new Date(value).toLocaleDateString()}</p>
          <p className="text-xs text-gray-300" style={pageStyles.textSecondary}>
            {formatDistanceToNow(new Date(value), { addSuffix: true })}
          </p>
        </div>
      ),
    },
  ]

  // Define table actions
  const actions: TableAction<PaymentLink>[] = [
    // {
    //   key: 'view',
    //   label: 'View',
    //   icon: <Eye className="w-4 h-4" />,
    //   onClick: record => {
    //     window.location.href = `/payment-links/${record.id}`
    //   },
    // },
    {
      key: 'copy',
      label: 'Copy',
      icon: <Copy className="mr-1 w-4 h-4" />,
      onClick: record => {
        copyToClipboard(record.payment_url)
      },
    },
  ]

  return (
    <div
      className="min-h-screen"
      style={pageStyles.container}
    >
      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white" style={pageStyles.text}>Payment Links</h1>
            <p className="text-gray-300 mt-1">Manage your payment links and track their usage</p>
          </div>
          <Button
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl" style={{ backgroundColor: statusColors.success.backgroundColor }}
            onClick={() => {
              window.location.href = '/payment-links/create'
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Payment Link
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 sm:mb-8">
          <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide text-gray-300 uppercase">
                    Total Links
                  </p>
                  <p className="mt-2 text-3xl font-bold text-white" style={pageStyles.text}>
                    {totalCount.toLocaleString()}
                  </p>
                  <p className="mt-1 text-xs text-gray-400" style={pageStyles.textSecondary}>All payment links</p>
                </div>
                <div className="p-4 bg-orange-500 shadow-lg rounded-2xl" style={{ backgroundColor: statusColors.warning.backgroundColor }}>
                  <LinkIcon className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide text-gray-300 uppercase">
                    Active Links
                  </p>
                  <p className="mt-2 text-3xl font-bold text-white" style={pageStyles.text}>
                    {paymentLinks.filter(link => link.is_active && !link.is_expired).length}
                  </p>
                  <p className="mt-1 text-xs text-gray-400" style={pageStyles.textSecondary}>Currently active</p>
                </div>
                <div className="p-4 bg-green-500 shadow-lg rounded-2xl" style={{ backgroundColor: statusColors.success.backgroundColor }}>
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide text-gray-300 uppercase">
                    Expired Links
                  </p>
                  <p className="mt-2 text-3xl font-bold text-white" style={pageStyles.text}>
                    {paymentLinks.filter(link => link.is_expired).length}
                  </p>
                  <p className="mt-1 text-xs text-gray-400" style={pageStyles.textSecondary}>No longer valid</p>
                </div>
                <div className="p-4 bg-orange-500 shadow-lg rounded-2xl" style={{ backgroundColor: statusColors.warning.backgroundColor }}>
                  <Clock className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide text-gray-300 uppercase">
                    Multiple Use
                  </p>
                  <p className="mt-2 text-3xl font-bold text-white" style={pageStyles.text}>
                    {paymentLinks.filter(link => link.multiple_use).length}
                  </p>
                  <p className="mt-1 text-xs text-gray-400" style={pageStyles.textSecondary}>Reusable links</p>
                </div>
                <div className="p-4 bg-blue-500 shadow-lg rounded-2xl" style={{ backgroundColor: statusColors.info.backgroundColor }}>
                  <TrendingUp className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-6 mb-8 transition-all duration-300 border card bg-slate-800/80 backdrop-blur-sm border-red-500/30" style={{ ...cardStyles, borderColor: statusColors.error.borderColor }}>
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-red-500/20 rounded-xl">
                <XCircle className="w-6 h-6 text-red-500" style={{ color: statusColors.error.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="mb-1 text-lg font-semibold text-white" style={pageStyles.text}>
                  Error loading payment links
                </h3>
                <p className="mb-4 text-sm text-red-200" style={pageStyles.textSecondary}>{error}</p>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      fetchPaymentLinks({
                        page: 1,
                        pageSize: pagination.pageSize,
                        sort,
                        filters,
                        search: '',
                      })
                    }
                    className="text-white bg-red-500/20 border-red-500/30 hover:bg-red-500/30" style={{ backgroundColor: statusColors.error.backgroundColor, borderColor: statusColors.error.borderColor }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setError(null)}
                    className="text-red-200 hover:text-white hover:bg-red-500/20" style={{ color: statusColors.error.color }}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
          <DataTable
            columns={columns}
            data={paymentLinks}
            loading={loading}
            pagination={pagination}
            filters={filters}
            sort={sort}
            actions={actions}
            rowKey="id"
            onSort={setSort}
            onFilter={setFilters}
            onPageChange={(page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize }))
              fetchPaymentLinks({
                page,
                pageSize,
                sort,
                filters,
                search: '',
              })
            }}
            onSearch={search => {
              fetchPaymentLinks({
                page: 1,
                pageSize: pagination.pageSize,
                sort,
                filters,
                search,
              })
            }}
            onServerSideChange={fetchPaymentLinks}
            serverSide={true}
            searchable={true}
            searchPlaceholder="Search payment links..."
            emptyText="No payment links found"
            emptyIcon={<LinkIcon className="w-12 h-12 text-gray-300" style={{ color: statusColors.info.color }} />}
            loadingText="Loading payment links..."
            showStats={true}
            showSearch={true}
            showFilters={true}
            showPagination={true}
            showActions={true}
            striped={true}
            hoverable={true}
            bordered={false}
            className="!bg-transparent !border-none !rounded-none"
          />
        </div>
      </div>
    </div>
  )
}
